import { GroupRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType, Switch } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useFormikContext } from 'formik';
import { useCreateSelectOptions } from 'hooks/useCreateSelectOptions';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { GroupFormType } from './GroupForm';

export const GroupBaseDataFormDocument = graphql(`
  fragment GroupBaseDataFormFields on Query {
    schoolYears(where: { organization: { uuid: $organizationUuid } }, options: { sort: { start: ASC } }) {
      __typename
      uuid
      name
    }
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: { selectName: ASC } }) {
      __typename
      uuid
      selectName
    }
    classes(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      divisionsConnection {
        edges {
          node {
            __typename
            uuid
          }
        }
      }
    }
    classGroups(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      division {
        __typename
        uuid
        name
      }
    }
  }
`);

type GroupBaseDataFormProps = {
  page: 'groups' | 'institution';
  onCollaborationChange: (isCollaboration: boolean) => void;
  query: FragmentType<typeof GroupBaseDataFormDocument> | undefined;
};

export const GroupBaseDataForm = ({ page, onCollaborationChange, query }: GroupBaseDataFormProps) => {
  const { t } = useTranslation();

  const isGroupsPage = page === 'groups';

  const { handleChange, setFieldValue, values, errors, setFieldTouched, handleBlur } =
    useFormikContext<GroupFormType>();

  const fragment = getFragmentData(GroupBaseDataFormDocument, query);
  const schoolYears = fragment?.schoolYears;
  const profiles = fragment?.profiles;

  const permissions = usePermissionChecker();

  const yearOptions = useCreateSelectOptions(schoolYears, 'uuid', 'name');
  const adminOptions = useCreateSelectOptions(profiles, 'uuid', 'selectName');

  const selectedAdmins: SelectOptionType[] = adminOptions.filter((o) =>
    values.admins.map((a) => a.profileUuid).includes(o.value as string),
  );

  const selectedSchoolYear = yearOptions.find((opt) => {
    return values.schoolYear ? opt.value === values.schoolYear : false;
  }) ?? { label: t('groups.schoolyearSpanning'), value: 'schoolyearSpanning' };

  return (
    <Grid useFormGap>
      <GridRow spacingBottom='s' headline={t('common.baseData')}>
        <Input
          autoFocus
          label={t('common.designation')}
          name='name'
          required
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          error={errors.name}
        />
      </GridRow>
      <GridRow spacingBottom='s' spacingTop='s' breakpoint='phone'>
        <GridColumn width={6}>
          <Select
            label={t('common.schoolyear')}
            name='schoolyear'
            options={yearOptions}
            required
            value={selectedSchoolYear}
            error={errors.schoolYear}
            isSearchable
            isClearable
            onChange={async (option) => {
              const value: SelectOptionType | null = option as SingleValue<SelectOptionType>;
              await setFieldTouched('schoolYear', true);
              await setFieldValue('schoolYear', value?.value);
            }}
            onBlur={handleBlur}
            menuPosition='fixed'
          />
        </GridColumn>
        <GridColumn width={6}>
          <Select
            name='admins'
            label={t('collaborations.administration')}
            value={selectedAdmins}
            options={adminOptions}
            onChange={async (event) => {
              const adminOptions = event as MultiValue<SelectOptionType>;
              await setFieldTouched('admins', true);
              await setFieldValue(
                'admins',
                adminOptions.map(({ value }) => {
                  return {
                    profileUuid: value,
                    role: GroupRoles.Admin,
                  };
                }),
              );
            }}
            isMulti
            isSearchable
            readonly={isGroupsPage}
            menuPosition='fixed'
            error={errors.admins as string}
          />
        </GridColumn>
      </GridRow>
      <GridRow spacingTop='s' spacingBottom='s' breakpoint='phone'>
        <GridColumn width={6} align='end' offset='left'>
          <Switch
            name='collaborating'
            label={t('groups.collaborationGroup')}
            checked={values.collaborating}
            disabled={isGroupsPage || !permissions?.canCreateCollaborationGroups()}
            onChange={(event) => {
              setFieldValue('collaborating', event.target.checked);
              onCollaborationChange(event.target.checked);
            }}
            tooltip={isGroupsPage ? t('collaborations.addHint') : undefined}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
